import { isAxiosError } from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { buildGalleryLevelPlan, getGalleryLevelPlanSet, getGalleryLevels, setGalleryLevelPlanSetDone, } from "../api/gallery/galleryLevels";
import { toggleDoneTick } from "../gallery/doneTick";
import { loadLevelBoughtTicks, saveLevelBoughtTicks } from "../gallery/marks";
import { backendErrorCode } from "../i18n/errorMessage";
/**
 * The milestones and the stored level plan, loaded per user and on `refresh`, a set's items on demand, marking a set
 * done, and a build that replaces the plan. `userUuid`: null signed out (the milestones alone), undefined while the
 * profile loads (nothing yet, so a signed-in user never sees the signed-out answer).
 */
export const useGalleryLevelPlan = (userUuid) => {
    const [levels, setLevels] = useState();
    const [status, setStatus] = useState("pending");
    const [building, setBuilding] = useState(false);
    // The failed build's error code, `UNKNOWN` for an uncoded failure.
    const [error, setError] = useState();
    // Buy cards ticked as bought since the last build.
    const [bought, setBought] = useState(loadLevelBoughtTicks);
    // The ticks as of the last toggle, ahead of the render; toggles read this.
    const boughtRef = useRef(bought);
    // A planned set's items by set id, null when the fetch failed; kept until a build replaces the plan.
    const [setItems, setSetItems] = useState({});
    // Set ids fetched or in flight for the current plan; a build swaps in a new Set.
    const requested = useRef(new Set());
    const [version, setVersion] = useState(0);
    // The user the levels on screen belong to ("" signed out): a refetch for them keeps those up until the new ones land.
    const loadedFor = useRef(undefined);
    const refresh = useCallback(() => setVersion((v) => v + 1), []);
    useEffect(() => {
        if (userUuid === undefined)
            return;
        const owner = userUuid ?? "";
        let cancelled = false;
        if (loadedFor.current !== owner)
            setStatus("pending");
        getGalleryLevels()
            .then((result) => {
            if (cancelled)
                return;
            loadedFor.current = owner;
            setLevels(result);
            setStatus("fulfilled");
        })
            .catch(() => {
            if (!cancelled && loadedFor.current !== owner)
                setStatus("rejected");
        });
        return () => {
            cancelled = true;
        };
    }, [userUuid, version]);
    const updateBought = (update) => {
        const next = update(boughtRef.current);
        boughtRef.current = next;
        saveLevelBoughtTicks(next);
        setBought(next);
    };
    // Resolves whether a new plan was stored.
    const build = async (level, settings) => {
        setBuilding(true);
        setError(undefined);
        try {
            const plan = await buildGalleryLevelPlan(level, settings);
            setLevels((prev) => prev && { ...prev, plan });
            requested.current = new Set();
            setSetItems({});
            // The new plan has the ticked cards as collected.
            updateBought(() => []);
            return true;
        }
        catch (e) {
            // A 402 raises the out-of-credits dialog instead.
            if (!isAxiosError(e) || e.response?.status !== 402)
                setError(backendErrorCode(e) ?? "UNKNOWN");
            return false;
        }
        finally {
            setBuilding(false);
        }
    };
    // Fetches a set's items once per plan; a failed fetch can be retried.
    const loadSetItems = useCallback((setId) => {
        const requests = requested.current;
        if (requests.has(setId))
            return;
        requests.add(setId);
        getGalleryLevelPlanSet(setId)
            .then((items) => {
            // A build since the request replaced the plan.
            if (requests === requested.current)
                setSetItems((prev) => ({ ...prev, [setId]: items }));
        })
            .catch(() => {
            if (requests !== requested.current)
                return;
            requests.delete(setId);
            setSetItems((prev) => ({ ...prev, [setId]: null }));
        });
    }, []);
    // Flips the set's done mark on the plan right away; a failed write reverts.
    const toggleSetDone = async (set) => {
        const done = !set.done;
        const apply = (value) => setLevels((prev) => prev?.plan && {
            ...prev,
            plan: {
                ...prev.plan,
                sets: prev.plan.sets.map((s) => s.setId === set.setId ? { ...s, done: value } : s),
            },
        });
        apply(done);
        try {
            await setGalleryLevelPlanSetDone(set.setId, done);
        }
        catch (e) {
            apply(!done);
            throw e;
        }
    };
    // Ticking adds the card as collected, un-ticking removes it.
    const toggleBought = (resourceId) => toggleDoneTick(resourceId, (id) => boughtRef.current.includes(id), (id, ticked) => updateBought((ids) => ticked
        ? ids.includes(id)
            ? ids
            : [...ids, id]
        : ids.filter((i) => i !== id)), !!userUuid);
    return {
        milestones: levels?.milestones ?? [],
        plan: levels?.plan,
        setCount: levels?.setCount,
        status,
        building,
        error,
        build,
        refresh,
        setItems,
        loadSetItems,
        bought,
        toggleBought,
        toggleSetDone,
    };
};
//# sourceMappingURL=useGalleryLevelPlan.js.map